import BackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import Spacings from '~/modules/ui-kit/spacings';

import ConfirmationModal from '~/components/ConfirmationModal';
import ContentView from '~/components/ContentView';
import NewsEntryForm from '~/containers/NewsEntryForm';
import { useDeleteNewsEntry } from '~/hooks/api/useDeleteNewsEntry';
import { useEditNewsEntry } from '~/hooks/api/useEditNewsEntry';
import { useNewsEntry } from '~/hooks/api/useNewsEntry';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import messages from './messages';

const route = getRouteApi('/news-entries/$newsEntryId');

const NewsEntryEdit = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { newsEntryId: newsEntryIdParam } = route.useParams();
  const search = route.useSearch();
  const newsEntryId = parseInt(newsEntryIdParam, 10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const handleBackButton = () => {
    navigate({ to: search.from || '/news-entries' });
  };

  const { data: newsEntry, isFetching } = useNewsEntry(newsEntryId);
  const newsEntryEditMutation = useEditNewsEntry(newsEntryId, {
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
      handleBackButton();
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });
  const newsEntryDeleteMutation = useDeleteNewsEntry(newsEntryId, {
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteNewsEntrySuccess),
      });
      handleBackButton();
      setIsDeleteModalOpen(false);
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.deleteNewsEntryFailed),
      });
      setIsDeleteModalOpen(false);
    },
  });

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <Spacings.Inline alignItems="center" style={{ flexGrow: 1 }}>
              <IconButton
                color="inherit"
                onClick={handleBackButton}
                size="large"
              >
                <BackIcon data-testid="back" />
              </IconButton>
              <div style={{ flexGrow: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'inherit',
                  }}
                >
                  <FormattedMessage {...messages.title} />
                </Typography>
              </div>

              <IconButton
                color="inherit"
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
                size="large"
              >
                <DeleteIcon data-testid="delete" />
              </IconButton>

              <ConfirmationModal
                open={isDeleteModalOpen}
                title={intl.formatMessage(messages.deleteNewsEntryConfirmTitle)}
                text={
                  <FormattedMessage {...messages.deleteNewsEntryConfirmText} />
                }
                confirmLabel={
                  <FormattedMessage
                    {...messages.deleteNewsEntryConfirmDelete}
                  />
                }
                cancelLabel={
                  <FormattedMessage
                    {...messages.deleteNewsEntryConfirmCancel}
                  />
                }
                onCancel={() => {
                  setIsDeleteModalOpen(false);
                }}
                onConfirm={() => {
                  newsEntryDeleteMutation.mutate();
                }}
              />
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      isLoading={newsEntryEditMutation.isPending}
    >
      {!isFetching && newsEntry && (
        <NewsEntryForm
          onSubmit={(values) =>
            newsEntryEditMutation.mutate({
              ...values,
              visibility: Object.keys(values.visibility).filter(
                (role) =>
                  values.visibility[role as keyof typeof values.visibility]
              ),
            })
          }
          initialValues={{
            ...newsEntry,
            visibility: newsEntry.visibility.reduce(
              (visibility, role) => ({
                ...visibility,
                [role]: true,
              }),
              {
                ShiftLeader: false,
                Vip: false,
                Publisher: false,
                Administrator: true,
              }
            ),
          }}
        />
      )}
    </ContentView>
  );
};

export default NewsEntryEdit;
